import React, { useEffect, useRef, useState } from "react"
import { Formik } from "formik"
import { Modal, ModalHeader, ModalBody, ModalFooter, Row, Col, Form, Input, Label, Button } from "reactstrap"
import { SaveToast } from "components/Common/SaveToast"
import { createChequeTemplate, updateChequeTemplate } from "helpers/backendHelpers/chequeTemplate"
import FieldPositionEditor from "./FieldPositionEditor"
import {
  DEFAULT_LEAF,
  cloneDefaultFields,
  REQUIRED_FIELDS,
  MAX_PRINTABLE_LEAF_WIDTH_MM,
  getTemplateGeometryErrors,
} from "./chequeFieldCatalogue"

const MAX_IMAGE_SIZE = 5 * 1024 * 1024

const buildInitialValues = template => {
  if (template) {
    return {
      bankName: template.bankName || "",
      imageUrl: template.imageUrl || "",
      leaf: template.leaf || DEFAULT_LEAF,
      fields: template.fields || cloneDefaultFields(),
    }
  }
  return {
    bankName: "",
    imageUrl: "",
    leaf: { ...DEFAULT_LEAF },
    fields: cloneDefaultFields(),
  }
}

// sourceTemplateId: admin is editing a customer template — saving publishes the edited result as a
// NEW default template (the customer's own template is left untouched).
const ChequeTemplateEditorModal = ({ isOpen, isEdit, template, toggle, onSaved, sourceTemplateId }) => {
  const [submitting, setSubmitting] = useState(false)
  const [selectedImageFile, setSelectedImageFile] = useState(null)
  const [previewUrl, setPreviewUrl] = useState(template?.imageUrl || "")
  const objectUrlRef = useRef(null)

  useEffect(() => {
    if (!isOpen) return
    if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current)
    objectUrlRef.current = null
    setSelectedImageFile(null)
    setPreviewUrl(template?.imageUrl || "")
  }, [isOpen, template])

  useEffect(() => () => {
    if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current)
  }, [])

  const handleImageSelect = event => {
    const file = event.target.files?.[0]
    event.target.value = ""
    if (!file) return

    if (!["image/jpeg", "image/png"].includes(file.type)) {
      SaveToast({ message: "Only JPG, JPEG, and PNG images are allowed.", type: "error" })
      return
    }
    if (file.size > MAX_IMAGE_SIZE) {
      SaveToast({ message: "Cheque leaf image must be 5 MB or smaller.", type: "error" })
      return
    }

    if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current)
    objectUrlRef.current = URL.createObjectURL(file)
    setSelectedImageFile(file)
    setPreviewUrl(objectUrlRef.current)
  }

  const isSaveDisabled = values =>
    submitting ||
    !previewUrl ||
    !values.bankName?.trim() ||
    !REQUIRED_FIELDS.every(key => values.fields.find(f => f.key === key)?.enabled) ||
    getTemplateGeometryErrors(values.fields, values.leaf).length > 0

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="xl">
      <ModalHeader toggle={toggle}>{sourceTemplateId ? "Edit & Save as Default Template" : isEdit ? "Edit Cheque Template" : "Add Cheque Template"}</ModalHeader>
      <Formik
        key={`${isOpen ? "open" : "closed"}-${template?._id || "new"}-${sourceTemplateId || ""}`}
        initialValues={buildInitialValues(template)}
        onSubmit={async values => {
          const payload = {
            bankName: values.bankName.trim(),
            leaf: values.leaf,
            fields: values.fields,
          }
          try {
            setSubmitting(true)
            let response
            if (isEdit && template?._id) {
              response = await updateChequeTemplate(template._id, payload, selectedImageFile)
            } else {
              response = await createChequeTemplate(payload, selectedImageFile, sourceTemplateId)
            }
            SaveToast({ message: response?.message || "Cheque template saved successfully.", type: "success" })
            onSaved()
            toggle()
          } catch (error) {
            SaveToast({ message: error?.response?.data?.message || "Unable to save cheque template.", type: "error" })
          } finally {
            setSubmitting(false)
          }
        }}
      >
        {({ values, handleChange, handleBlur, handleSubmit, setFieldValue }) => (
          <>
            <ModalBody>
              <Form
                onSubmit={e => {
                  e.preventDefault()
                  handleSubmit(e)
                  return false
                }}
              >
                <Row className="mb-3">
                  <Col md={12}>
                    <Label className="form-label">
                      Bank Name<span className="text-danger">*</span>
                    </Label>
                    <Input name="bankName" value={values.bankName} onChange={handleChange} onBlur={handleBlur} />
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md={6}>
                    <Label className="form-label">
                      Cheque Leaf Image<span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="file"
                      accept="image/jpeg,image/png,.jpg,.jpeg,.png"
                      onChange={handleImageSelect}
                      disabled={submitting}
                    />
                    <small className="text-muted d-block mt-1">JPG, JPEG, or PNG only; maximum 5 MB. The image uploads when you save.</small>
                  </Col>
                  <Col md={3}>
                    <Label className="form-label">Leaf Width (mm)</Label>
                    <Input
                      type="number"
                      min={150}
                      max={MAX_PRINTABLE_LEAF_WIDTH_MM}
                      step={0.5}
                      value={values.leaf.widthMm}
                      onChange={e => setFieldValue("leaf", { ...values.leaf, widthMm: parseFloat(e.target.value) || values.leaf.widthMm })}
                    />
                  </Col>
                  <Col md={3}>
                    <Label className="form-label">Leaf Height (mm)</Label>
                    <Input
                      type="number"
                      min={70}
                      max={110}
                      step={0.5}
                      value={values.leaf.heightMm}
                      onChange={e => setFieldValue("leaf", { ...values.leaf, heightMm: parseFloat(e.target.value) || values.leaf.heightMm })}
                    />
                  </Col>
                </Row>

                {previewUrl ? (
                  <FieldPositionEditor
                    imageUrl={previewUrl}
                    leaf={values.leaf}
                    fields={values.fields}
                    onFieldsChange={fields => setFieldValue("fields", fields)}
                  />
                ) : (
                  <div className="alert alert-info mb-0">Upload a cheque leaf image above to position the fields.</div>
                )}
                {getTemplateGeometryErrors(values.fields, values.leaf)[0] && (
                  <div className="alert alert-danger mt-2 mb-0">
                    {getTemplateGeometryErrors(values.fields, values.leaf)[0]}
                  </div>
                )}
              </Form>
            </ModalBody>
            <ModalFooter>
              <Button color="secondary" outline onClick={toggle} type="button">
                Cancel
              </Button>
              <Button color="success" onClick={handleSubmit} disabled={isSaveDisabled(values)}>
                {isEdit ? "Update" : sourceTemplateId ? "Save as Default" : "Save"}
              </Button>
            </ModalFooter>
          </>
        )}
      </Formik>
    </Modal>
  )
}

export default ChequeTemplateEditorModal
